import { useEffect, useRef, useState } from 'react';
import { getVipClicks } from '../services/utmService';

const PAGE_SIZE = 20;

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

function platformClass(platform) {
  const value = (platform || '').toLowerCase();
  if (value === 'ios') return 'platform-ios';
  if (value === 'android') return 'platform-android';
  return 'platform-other';
}

function platformLabel(platform) {
  const value = (platform || '').toLowerCase();
  if (value === 'ios') return 'iOS';
  if (value === 'android') return 'Android';
  return platform || '—';
}

export default function UtmTracking() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState('newest');

  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadClicks() {
      setLoading(true);
      setError('');

      try {
        const response = await getVipClicks({ page, limit: PAGE_SIZE, sort });

        if (cancelled) return;

        setItems(Array.isArray(response.data) ? response.data : []);
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch VIP click events.');
        setItems([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadClicks();
    return () => {
      cancelled = true;
    };
  }, [page, sort]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  const pageItems = getPageItems(page, totalPages);

  return (
    <div className="payments-page main-bg-top">
      <div className="users-header utm-header">
        <div className="users-filters">
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      <div className="payments-table-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table payments-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Phone</th>
                <th>Platform</th>
                <th>Device</th>
                <th>App / OS</th>
                <th>Locale</th>
                <th>Network</th>
                <th>IP Address</th>
                <th>Clicked At</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="users-empty">Loading VIP click events...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="9" className="users-empty">{error}</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="9" className="users-empty">No VIP click events found.</td>
                </tr>
              ) : (
                items.map((item) => {
                  const name = item.user?.name || item.name || '—';
                  const email = item.user?.email || item.email || '';
                  const phone = item.user?.phone || '—';

                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {item.user?.image?.url ? (
                              <img
                                src={item.user.image.url}
                                alt={name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.textContent = name !== '—' ? name.charAt(0).toUpperCase() : '?';
                                }}
                              />
                            ) : (
                              <span className="avatar-placeholder">
                                {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                              </span>
                            )}
                          </div>
                          <div className="user-cell-info">
                            <span className="user-cell-name">{name}</span>
                            <span className="user-cell-email">{email}</span>
                          </div>
                        </div>
                      </td>
                      <td>{phone}</td>
                      <td>
                        <span className={`status-badge ${platformClass(item.platform)}`}>
                          {platformLabel(item.platform)}
                        </span>
                      </td>
                      <td className="utm-device-cell">
                        <div className="datetime-cell">
                          <span className="datetime-date">{item.deviceModel || '—'}</span>
                          <span className="datetime-time" title={item.deviceId || ''}>
                            {item.deviceId || ''}
                          </span>
                        </div>
                      </td>
                      <td className="utm-nowrap-cell">
                        <div className="datetime-cell">
                          <span className="datetime-date">{item.appVersion ? `v${item.appVersion}` : '—'}</span>
                          <span className="datetime-time">{item.osVersion ? `OS ${item.osVersion}` : ''}</span>
                        </div>
                      </td>
                      <td>{item.locale || '—'}</td>
                      <td>{item.networkType || '—'}</td>
                      <td className="utm-nowrap-cell" title={item.userAgent || ''}>
                        {item.ipAddress || '—'}
                      </td>
                      <td className="utm-nowrap-cell">
                        <div className="datetime-cell">
                          <span className="datetime-date">{formatDate(item.clickedAt)}</span>
                          <span className="datetime-time">{formatTime(item.clickedAt)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="payments-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
